import { useCallback, useState } from "react";
import type * as React from "react";
import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { ArrowBack } from "@mui/icons-material";
import GettingStartedSurveyExitConfirmationModal from "components/v3/sharedComponents/modals/GettingStartedSurveyExitConfirmationModal";
import { COLORS } from "components/v3/Theme/colors";
import HeaderLogo from "assets/images/GL-Logo.svg";
import PageTitle from "utils/PageTitle";
import Translate from "translate/Translate";

const SURVEY_AREA_MIN_HEIGHT = 280;

const hiddenSurveySlotSx = {
  height: "1px",
  left: "-10000px",
  overflow: "hidden",
  position: "absolute",
  top: "auto",
  width: "1px"
} as const;

export const STANDALONE_SURVEY_SURFACE_CLASS = "standalone-survey-surface";

export const standaloneSurfaceCardSx = {
  backgroundColor: COLORS.WHITE,
  border: `1px solid ${COLORS.GRADIENT_START}`
} as const;

const standaloneSurfaceDescendantSx = {
  [`& .${STANDALONE_SURVEY_SURFACE_CLASS}`]: standaloneSurfaceCardSx
} as const;

const visibleSurveySlotSx = {
  display: "flex",
  flex: "1 1 0%",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  width: "100%",
  ...standaloneSurfaceDescendantSx
} as const;

export const getStandaloneSurveySlotSx = (isHidden: boolean): SxProps<Theme> =>
  isHidden ? hiddenSurveySlotSx : visibleSurveySlotSx;

export const getStandaloneSurveyRootSx = (
  isSummaryVisible: boolean
): SxProps<Theme> =>
  isSummaryVisible
    ? { width: "100%", ...standaloneSurfaceDescendantSx }
    : visibleSurveySlotSx;

type StandaloneSurveyPageLayoutProps = {
  children: React.ReactNode;
  exitDescriptionText?: string;
  exitTo?: string;
  maxWidth?: number;
  onExitClick?: () => void;
  onExitModalClose?: () => void;
  pageTitle: string;
  showLogoHeader?: boolean;
};

const StandaloneSurveyPageLayout: React.FC<StandaloneSurveyPageLayoutProps> = ({
  children,
  exitDescriptionText,
  exitTo,
  maxWidth = 800,
  onExitClick,
  onExitModalClose,
  pageTitle,
  showLogoHeader = false
}) => {
  const [exitConfirmationModalOpen, setExitConfirmationModalOpen] =
    useState(false);

  PageTitle(pageTitle);

  const handleExitClick = useCallback(() => {
    onExitClick?.();
    setExitConfirmationModalOpen(true);
  }, [onExitClick]);

  const handleCloseModal = useCallback(() => {
    onExitModalClose?.();
    setExitConfirmationModalOpen(false);
  }, [onExitModalClose]);

  return (
    <Box
      sx={{
        background:
          "linear-gradient(180deg, #F2F7FB 0%, rgba(242, 247, 251, 0.00) 62.6%)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        overflow: "auto",
        pb: "40px",
        position: "relative"
      }}
    >
      <Box
        sx={{
          left: {
            xs: "12px",
            sm: "28px",
            lg: "64px"
          },
          position: "absolute",
          top: "38px",
          zIndex: 1
        }}
      >
        <Box
          component="button"
          onClick={handleExitClick}
          type="button"
          sx={{
            alignItems: "center",
            background: "none",
            border: 0,
            color: "inherit",
            cursor: "pointer",
            display: "flex",
            font: "inherit",
            gap: {
              xs: "4px",
              lg: "12px"
            },
            padding: 0
          }}
        >
          <ArrowBack
            sx={{
              color: COLORS.DARK_GRAY,
              height: "20px",
              width: "20px"
            }}
          />
          <Typography variant="body_strong_dark_gray">
            <Translate text="Exit" />
          </Typography>
        </Box>
      </Box>

      {showLogoHeader && (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexShrink: 0,
            justifyContent: "center",
            py: 3
          }}
        >
          <Box
            alt="Cancer Guardian by Genomic Life logo"
            component="img"
            src={HeaderLogo}
            sx={{ maxHeight: "41px", maxWidth: "175px" }}
          />
        </Box>
      )}

      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          marginX: "auto",
          maxWidth,
          minHeight: SURVEY_AREA_MIN_HEIGHT,
          pt: showLogoHeader
            ? 0
            : {
                md: 10,
                xs: 9
              },
          px: {
            xs: 2,
            sm: 4,
            lg: 0
          },
          width: "100%",
          ...standaloneSurfaceDescendantSx
        }}
      >
        {children}
      </Box>

      <GettingStartedSurveyExitConfirmationModal
        descriptionText={exitDescriptionText}
        exitTo={exitTo}
        isOpen={exitConfirmationModalOpen}
        onCloseModal={handleCloseModal}
      />
    </Box>
  );
};

export default StandaloneSurveyPageLayout;
